
import React from 'react';
import { User, Camera } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Child } from '@/types/child';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/utils/logger';

interface ChildProfileCardProps {
  child: Child;
  onUpdatePhoto: (file: File) => Promise<void>;
}

const ChildProfileCard: React.FC<ChildProfileCardProps> = ({ child, onUpdatePhoto }) => {
  logger.log('Rendering ChildProfileCard with data:', child);
  const { toast } = useToast();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  
  // Calculate age properly
  const calculateAge = () => {
    if (!child.birthdate) return 'Unknown';
    
    try {
      const birthDate = new Date(child.birthdate);
      const today = new Date();
      
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      return age;
    } catch (e) {
      logger.error('Error calculating age:', e);
      return 'Unknown';
    }
  };

  // Format date correctly accounting for timezone offset
  const formatBirthdate = (dateString: string) => {
    if (!dateString) return 'Unknown';
    
    try {
      // Create date object and adjust for local timezone
      const date = new Date(dateString);
      // Add the timezone offset to get the correct date
      const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
      return utcDate.toLocaleDateString();
    } catch (e) {
      logger.error('Error formatting date:', e);
      return 'Unknown';
    }
  };

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please select an image file (JPEG, PNG, etc.)",
        variant: "destructive"
      });
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please select an image smaller than 5MB",
        variant: "destructive"
      });
      return;
    }

    try {
      await onUpdatePhoto(file);
    } catch (error) {
      logger.error('Error uploading photo:', error);
      toast({
        title: "Error",
        description: "Failed to update photo. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <Card className="md:col-span-1">
      <div className="relative">
        <AspectRatio ratio={1}>
          <div className="w-full h-full overflow-hidden bg-gray-200 flex items-center justify-center">
            {child.photoUrl ? (
              <Avatar className="w-full h-full rounded-none">
                <AvatarImage 
                  src={child.photoUrl} 
                  alt={child.name} 
                  className="object-cover w-full h-full"
                />
                <AvatarFallback className="w-full h-full rounded-none">
                  <User className="h-20 w-20 text-gray-400" />
                </AvatarFallback>
              </Avatar>
            ) : (
              <User className="h-20 w-20 text-gray-400" />
            )}
          </div>
        </AspectRatio>
        <div className="absolute bottom-2 w-full flex justify-center"> {/* Changed positioning */}
          <Button 
            variant="secondary" 
            size="sm" 
            className="bg-background/80 backdrop-blur-sm"
            onClick={handlePhotoClick}
          >
            <Camera className="h-4 w-4 mr-1" />
            {child.photoUrl ? 'Change' : 'Add Photo'}
          </Button>
        </div>
        <input 
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept="image/*"
          onChange={handleFileChange}
        />
      </div>
      <CardContent className="pt-6">
        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-500">Name</p>
            <p className="font-medium">{child?.name || 'Unknown'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Birthday</p>
            <p className="font-medium">
              {child?.birthdate ? formatBirthdate(child.birthdate) : 'Unknown'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Age</p>
            <p className="font-medium">
              {typeof calculateAge() === 'number' ? `${calculateAge()} years` : calculateAge()}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ChildProfileCard;
